/** @format */

import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import FilterComponent from "../Common/FilterComponent";
import { useSelector, useDispatch } from "react-redux";
import {
  addProduct,
  deleteProduct,
  fetchProducts,
} from "../../ReduxToolkit/Reducers/ProductsReducer";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FormGroup, Input } from "reactstrap";
import { Badges, Image, P } from "../../AbstractElements";
import { Link } from "react-router-dom";
import SvgIcon from "../../Utils/CommonComponents/CommonIcons/CommonSvgIcons";
import QuickEdit from "./QuickEditModal";
import { getFormattedPrice } from "../../services/helper";
import BulkEditModal from "./BulkEditModal"; // Import the BulkEditModal
import { BsCopy } from "react-icons/bs";
import Loader from "../../Layout/Loader";
import { openViewWithParams } from "../../ReduxToolkit/Reducers/viewReducer";

export default function ProductListTable() {
  const [Product, setProduct] = useState({});
  const [filterText, setFilterText] = useState("");
  const [Edit, setEdit] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState([]); // Track selected products
  const { products, categories, isLoading } = useSelector(
    (state) => state.products
  );
  const dispatch = useDispatch();

  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false); // State to control the Bulk Edit modal

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);

    if (isChecked) {
      const allFilteredIds = filteredItems.map((item) => item.id);
      setSelectedProducts(allFilteredIds);
    } else {
      setSelectedProducts([]);
    }
  };

  const productListColumns = [
    {
      name: (
        <FormGroup check>
          <Input
            className="checkbox-primary"
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          />
        </FormGroup>
      ),
      cell: (row) => (
        <FormGroup check>
          <Input
            className="checkbox-primary"
            type="checkbox"
            checked={selectedProducts.includes(row.id)}
            onChange={(e) => handleSelectProduct(e, row.id)}
          />
        </FormGroup>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Product Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="product-names">
          <div className="light-product-box">
            <Image className="img-fluid" src={row.images[0]} alt="" />
          </div>
          <P>{row.name}</P>
        </div>
      ),
      width: "30%",
    },
    {
      name: "SKU",
      selector: (row) => row.sku,
      sortable: true,
      cell: (row) => row.sku,
    },
    {
      name: "Stock",
      selector: (row) => row.stock_quantity,
      sortable: true,
      cell: (row) => (
        <Badges color={`light-info`}>
          {row.in_stock ? "In stock" : row.stock_quantity}
        </Badges>
      ),
    },
    {
      name: "Cost",
      selector: (row) => row.product_cost,
      sortable: true,
      cell: (row) => (
        <P className="f-light">{getFormattedPrice(row.product_cost)}</P>
      ),
    },
    {
      name: "Regular Price",
      selector: (row) => row.regular_price,
      sortable: true,
      cell: (row) => (
        <P className="f-light">{getFormattedPrice(row.regular_price)}</P>
      ),
    },

    {
      name: "Variable",
      selector: (row) => row.is_pos_variable,
      sortable: true,
      cell: (row) => (
        <Badges color={`light-info`} className={`txt-info`}>
          {row.is_pos_variable ? "Yes" : <span className="text-muted">No</span>}
        </Badges>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="product-action">
          <BsCopy
            role="button"
            className="font-hover me-1"
            onClick={() => handleDupliateProduct(row)}
          />

          <i
            role="button"
            className="icon-eye font-hover me-1"
            onClick={() => handleQuickEdit(row)}
          ></i>
          <Link
            onClick={() =>
              dispatch(
                openViewWithParams({
                  view: row.is_pos_variable
                    ? "products_edit_variable"
                    : "products_edit",
                  params: { product_id: row.id, product: row },
                })
              )
            }
          >
            <SvgIcon iconId="edit-content" />
          </Link>
          <SvgIcon iconId="trash1" onClick={() => handleDelete(row.id)} />
        </div>
      ),
      sortable: true,
    },
  ];

  useEffect(() => {
    if (products.length === 0) {
      dispatch(fetchProducts());
    }
  }, []);

  const filteredItems = products.filter((item) => {
    return Object.values(item).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(filterText.toLowerCase())
    );
  });

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--theme-red)",
      cancelButtonColor: "var(--theme-blue)",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        dispatch(deleteProduct(id));
        toast.success("Product deleted successfully");
      } catch (error) {
        toast.error("Error deleting product");
        console.log(error);
      }
    }
  };

  const handleDupliateProduct = async (product) => {
    const result = await Swal.fire({
      title: "Duplicate new product?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--theme-red)",
      cancelButtonColor: "var(--theme-blue)",
      confirmButtonText: "Yes, Duplicate it!",
    });

    if (result.isConfirmed) {
      try {
        const randomSuffix = Math.floor(1000 + Math.random() * 9000); // Generates 4-digit number
        const new_product = {
          ...product,
          sku: `${product.sku}_${randomSuffix}`,
        };
        delete new_product.id;

        dispatch(addProduct(new_product));
        toast.success("Product duplicated successfully");
      } catch (error) {
        toast.error("Error duplicating product");
        console.error(error);
      }
    }
  };

  const handleBulkDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedProducts.length} products! This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!",
    }).then((result) => {
      if (result.isConfirmed) {
        selectedProducts.forEach((productId) => {
          dispatch(deleteProduct(productId));
          toast.success(`Product ${productId} deleted`);
        });
        setSelectedProducts([]);
      }
    });
  };

  const handleSelectProduct = (e, productId) => {
    let updatedSelected;
    if (e.target.checked) {
      updatedSelected = [...selectedProducts, productId];
    } else {
      updatedSelected = selectedProducts.filter((id) => id !== productId);
    }

    setSelectedProducts(updatedSelected);

    // Sync selectAll state
    setSelectAll(updatedSelected.length === filteredItems.length);
  };

  const handleQuickEdit = (product) => {
    setProduct(product);
    setEdit(true);
  };

  const handleBulkEdit = () => {
    setBulkEditModalOpen(true); // Open the bulk edit modal
  };

  return (
    <div className="list-product product-list">
      {isLoading && <Loader />}

      <QuickEdit Product={Product} setEdit={setEdit} Edit={Edit} />

      <div className="d-flex  align-items-center mb-3">
        <FilterComponent
          onFilter={(e) => setFilterText(e.target.value)}
          filterText={filterText}
        />

        {selectedProducts.length > 0 && (
          <div style={{ marginTop: "-20px" }}>
            {selectedProducts.length > 1 && (
              <>
                <button
                  className="btn btn-danger ms-2"
                  onClick={handleBulkDelete}
                >
                  Delete
                </button>
                <button
                  className="btn btn-primary ms-2"
                  onClick={handleBulkEdit}
                >
                  {" "}
                  {/* Call the bulk edit handler */}
                  Bulk Edit
                </button>
                <a className="btn ms-2" onClick={() => setSelectedProducts([])}>
                  {" "}
                  {/* Call the bulk edit handler */}
                  Cancel
                </a>
              </>
            )}
          </div>
        )}
      </div>

      <DataTable
        className="custom-scrollbar"
        data={filteredItems}
        columns={productListColumns}
        pagination
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={bulkEditModalOpen}
        onToggle={() => setBulkEditModalOpen(false)} // Toggle modal visibility
        selectedProducts={selectedProducts.map((id) =>
          products.find((p) => p.id === id)
        )}
      />
    </div>
  );
}
